// Line: what a character says, or a silence, as an item with three stages (src/ui/stages): 1 Voice (who says it, typed for an AI voice
// or recorded on the mic, and every take of it; see VoiceMaker.tsx), 2 Expressions (how each face in the picture looks as the
// line starts) and 3 Camera. A silent line is the same item, titled "Silence", with How set to Silence on its Voice stage. A new line opens on Voice; an existing one on its Overview, which says what each stage holds
// and has the line's own actions at the foot (a new line after it, Earlier, Later, Delete).
// Nothing is confirmed: the line goes onto the timeline at once as a "syncing" line when its voice is asked for (or its
// recording made), and is made behind it (see makeClip.ts), so the other stages can be used, or the page left, while it is
// made. Expressions and Camera wait until the line is on the timeline; Expressions also need someone on screen as it starts,
// and Camera a picture. Picking a take, an expression or a camera saves it there and then.
import { useState } from "react";
import { useLibrary } from "../../../library/store";
import { findAsset } from "../../../model/library";
import { cameraAt, feelingAt, lineById, marksOf, pictureAt, type PlacedLine } from "../../../model/timeline";
import { FrameSvg } from "../../../render/FrameSvg";
import { Avatar, Icon, secs, useIdleClock } from "../../common";
import { OverviewPeek, OverviewRow, OverviewRows, StagePage, useStageGo, type Stage, type StageAt } from "../../stages/StagePage";
import { useTimeline } from "../TimelineLoader";
import { LineActions } from "./LineActions";
import { LineCamera, LineFeelings, lineHasCamera, linePeople } from "./LineStartSections";
import { frameWith, subLine } from "./shared";
import { VoiceMaker } from "./VoiceMaker";
import type { PageProps } from "./index";

type Step = "voice" | "feel" | "camera";
const STEPS: readonly string[] = ["overview", "voice", "feel", "camera"];
// Callers may still say "line" or "take" (the two steps that became Voice).
const stepOf = (s: string | undefined): StageAt<Step> | undefined => (s === "line" || s === "take" ? "voice" : STEPS.includes(s ?? "") ? (s as StageAt<Step>) : undefined);

export function LinePage({ t, editId, newId, char, step, onClose, onOpen, onSeek }: PageProps) {
  const { ep } = useTimeline();
  const lib = useLibrary();
  const id = (editId ?? newId)!;
  const clip = lineById(ep, id); // spoken or silent
  const [at, setAt] = useState<StageAt<Step>>(stepOf(step) ?? (editId ? "overview" : "voice"));
  const [error, setError] = useState<string | null>(null);
  const silent = clip?.how === "silent";
  const name = clip && !silent ? findAsset(lib, clip.char ?? "")?.name ?? "?" : undefined;
  const people = clip ? linePeople(ep, clip.id) : [];
  const pic = clip ? pictureAt(ep, clip.start) : undefined;
  const startsAt = (kind: "feeling" | "camera") => !!clip && marksOf(ep, kind).some(m => Math.abs(m.t - clip.start) < 1e-3);

  // The two stages after Voice wait until the line is on the timeline, and then for what they work on.
  const unmade = { reason: "Opens once the voice has been asked for. The line goes on the timeline then." };
  const stages: Stage<Step>[] = [
    { key: "voice", label: "Voice", done: clip?.status === "ready", working: clip?.status === "syncing" },
    { key: "feel", label: "Expressions", done: startsAt("feeling"),
      wait: !clip ? unmade : people.length ? undefined : {
        reason: pic ? "Expressions opens once someone is on screen as the line starts. Nobody is in this picture yet." : "Expressions opens once someone is on screen as the line starts. There is no picture here yet, so add one first.",
        action: pic && <button className="btn small" onClick={() => onOpen("picture", { t: pic.t, editId: pic.id, step: "faces" })}><Icon name="person" size={16} />Put {name ?? "someone"} in this picture</button>,
      } },
    { key: "camera", label: "Camera", done: startsAt("camera"),
      wait: !clip ? unmade : lineHasCamera(ep, clip.id) ? undefined : { reason: "Camera opens once there is a picture here to frame. Add a picture first." } },
  ];

  return (
    <StagePage title={silent ? "Silence" : name ? `${name}'s line` : "New line"} sub={subLine(clip?.start ?? t, !!clip)} onBack={onClose} onDone={onClose}
      stages={stages} at={at} onAt={setAt} className={at === "voice" ? "mk" : undefined} toast={error} clearToast={() => setError(null)}>
      {at === "overview" && (clip ? <LineOverview clip={clip} stages={stages} onClose={onClose} onOpen={onOpen} onSeek={onSeek} /> : <p className="hint">This line is gone.</p>)}
      {at === "voice" && <VoiceMaker t={t} id={id} char={char} onOpen={onOpen} onSeek={onSeek} onError={setError} />}
      {at === "feel" && clip && <LineFeelings lineId={clip.id} />}
      {at === "camera" && clip && <LineCamera lineId={clip.id} />}
    </StagePage>
  );
}

// What each stage holds now, one row each, then the line's own actions.
function LineOverview({ clip, stages, onClose, onOpen, onSeek }: { clip: PlacedLine; stages: Stage<Step>[] } & Pick<PageProps, "onClose" | "onOpen" | "onSeek">) {
  const { ep } = useTimeline();
  const lib = useLibrary();
  const clock = useIdleClock();
  const go = useStageGo<Step>();
  const nameOf = (c: string) => findAsset(lib, c)?.name ?? "?";
  const waiting = (k: Step) => !!stages.find(s => s.key === k)?.wait;
  const people = linePeople(ep, clip.id);
  const cam = cameraAt(ep, clip.start);

  // "V3 · 2.4 s · “Not the toaster again!”", or "Recorded · 2.4 s" for a recording.
  const take = clip.takes?.findIndex(tk => tk.audio === clip.audio) ?? -1;
  const voice = clip.how === "silent" ? `Silence · ${clip.duration.toFixed(1)} s` : clip.status === "syncing" ? "Making the voice…" : clip.status === "error" ? "Didn't work" : clip.text !== undefined && clip.how !== "recorded"
    ? `${take >= 0 ? `V${take + 1} · ` : ""}${secs(clip.duration)} · “${clip.text.trim()}”` : `Recorded · ${secs(clip.duration)}`;
  const camera = !lineHasCamera(ep, clip.id) ? "No picture here" : cam.framing === "whole" ? "Whole picture"
    : cam.framing === "free" || !cam.on ? "Framed by hand" : `${nameOf(cam.on)}, ${cam.framing === "close" ? "close-up" : "medium"}`;

  return (
    <>
      <OverviewPeek><FrameSvg id="tl-lineover" boil={false} frame={frameWith(ep, clip.start, clock)} /></OverviewPeek>
      <OverviewRows>
        <OverviewRow round thumb={clip.how === "silent" ? <Icon name="silence" size={24} /> : <Avatar assetId={clip.char ?? ""} size={44} />} title="Voice" sub={voice} onClick={() => go("voice")} />
        <OverviewRow round thumb={people[0] ? <Avatar assetId={people[0]} size={44} emotion={feelingAt(ep, people[0], clip.start)} /> : <Icon name="face" size={24} />} title="Expressions"
          sub={people.length ? people.map(c => `${nameOf(c)} ${feelingAt(ep, c, clip.start)}`).join(" · ") : "Nobody on screen"} waiting={waiting("feel")} onClick={() => go("feel")} />
        <OverviewRow round thumb={<Icon name="video" size={24} />} title="Camera" sub={camera} waiting={waiting("camera")} onClick={() => go("camera")} />
      </OverviewRows>
      <LineActions lineId={clip.id} onClose={onClose} onOpen={onOpen} onSeek={onSeek} />
    </>
  );
}
